
/**
 * BaseController는 모든 Controller의 기본이 되는 클래스입니다.
 * 비즈니스 로직을 처리하는 기본적인 메서드들을 정의합니다.
 * 싱글톤 패턴을 구현하여 하나의 인스턴스만 존재하도록 합니다.
 */
export class BaseController {
  /**
   * BaseController 생성자
   * @param {Object} repository - Repository 인스턴스
   */
  constructor(repository = null) {
    if (this.constructor === BaseController) {
      throw new Error('BaseController는 직접 인스턴스화할 수 없습니다.');
    }
    this.repository = repository;
  }

  /**
   * 컨트롤러의 인스턴스를 가져옵니다.
   * @param {Object} repository - 리포지토리 인스턴스
   * @returns {BaseController} 컨트롤러 인스턴스
   */
  static getInstance(repository) {
    if (!this.instance) {
      this.instance = new this(repository);
    }
    return this.instance;
  }

  /**
   * 데이터를 생성하는 비즈니스 로직을 처리합니다.
   * @param {Object} data - 생성할 데이터
   * @returns {Promise<Object>} 생성된 데이터
   */
  async create(data) {
    if (!this.repository) {
      throw new Error('Repository가 초기화되지 않았습니다.');
    }
    return await this.repository.create(data);
  }

  /**
   * 데이터를 조회하는 비즈니스 로직을 처리합니다.
   * @param {string|number} id - 조회할 데이터의 ID
   * @returns {Promise<Object>} 조회된 데이터
   */
  async getById(id) {
    if (!this.repository) {
      throw new Error('Repository가 초기화되지 않았습니다.');
    }
    return await this.repository.getById(id);
  }

  /**
   * 모든 데이터를 조회하는 비즈니스 로직을 처리합니다.
   * @returns {Promise<Array>} 조회된 데이터 목록
   */
  async getAll() {
    if (!this.repository) {
      throw new Error('Repository가 초기화되지 않았습니다.');
    }
    return await this.repository.getAll();
  }

  /**
   * 데이터를 업데이트하는 비즈니스 로직을 처리합니다.
   * @param {string|number} id - 업데이트할 데이터의 ID
   * @param {Object} data - 업데이트할 데이터
   * @returns {Promise<Object>} 업데이트된 데이터
   */
  async update(id, data) {
    if (!this.repository) {
      throw new Error('Repository가 초기화되지 않았습니다.');
    }
    return await this.repository.update(id, data);
  }

  /**
   * 데이터를 삭제하는 비즈니스 로직을 처리합니다.
   * @param {string|number} id - 삭제할 데이터의 ID
   * @returns {Promise<boolean>} 삭제 성공 여부
   */
  async delete(id) {
    if (!this.repository) {
      throw new Error('Repository가 초기화되지 않았습니다.');
    }
    return await this.repository.delete(id);
  }
} 